import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ContactService } from "@services";
import utils from "@utils";
import Loader from "../Loader/Loader";
import Modal from "../Modal/Modal";
import AddContactModal from "../AddContactModal/AddContactModal";

import "./SendToContactModal.scss";

export class SendToContactModal extends Component {
	constructor(props) {
		super(props);

		this.initialState = {
			onHold: false,
			loading: false,
			submittingInvoice: false,
			showAddContactModal: false,
			billingContact: {
				label: "",
				value: "",
			},
			additionalContact: "",
			inputHasError: false,
			additionalContacts: [],
			hasError: false,
			clientContacts: [],
		};

		this.state = { ...this.initialState };
	}

	removeBillingContact = () =>
		this.setState({ billingContact: this.initialState.billingContact });

	selectBillingContact = event => {
		const { clientContacts } = this.state;

		const billingContact = clientContacts.find(
			item => item.value === parseInt(event.target.value, 10),
		);
		this.setState({ billingContact });
	};

	openAddContactModal = () => {
		this.setState({
			showAddContactModal: true,
		});
	};

	onExitAddContactModal = () => {
		this.setState(
			{
				showAddContactModal: false,
				onHold: false,
			},
			() => {
				this.getClientContacts();
			},
		);
	};

	/** Retrieves client contact details for a specific client */
	getClientContacts = () => {
		const { clientId } = this.props;
		const { lang } = global;

		this.setState({ loading: true });
		ContactService.getAll({ clientId, isOrganization: false })
			.then(({ data }) => {
				const { ItemList, BillingContactId } = data;
				const clientContacts = ItemList.map(({ Id: value, FirstName, LastName }) => ({
					value,
					label: `${FirstName} ${LastName}`,
				}));

				this.setState({
					clientContacts,
					loading: false,
					billingContact: clientContacts.some(item => item.value === BillingContactId)
						? clientContacts.find(item => item.value === BillingContactId)
						: { label: "", value: "" },
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading contacts"),
				});
			});
	};

	/** Gathers and validates all required information for sending an invoice to a contacts email address */
	handleSubmit = () => {
		const { onSubmit, isOwnAgency } = this.props;
		const { billingContact, additionalContacts } = this.state;
		if (isOwnAgency && billingContact.value === "") {
			this.setState({ hasError: true });
		} else {
			this.setState({ submittingInvoice: true });
			onSubmit(billingContact.value, additionalContacts);
		}
	};

	/** Adds additonal inputs which lets the user add additional contacts */
	onSubmitAdditionalContact = () => {
		this.setState(prevState => {
			if (
				/\S+@\S+\.\S+/.test(prevState.additionalContact) &&
				!prevState.additionalContacts.includes(prevState.additionalContact)
			) {
				return {
					additionalContacts: [
						...prevState.additionalContacts,
						prevState.additionalContact,
					],
					additionalContact: "",
				};
			}
			return { inputHasError: true };
		});
	};

	removeAdditionalContact = email => {
		this.setState(prevState => ({
			additionalContacts: prevState.additionalContacts.filter(item => item !== email),
		}));
	};

	render() {
		const { lang } = global;
		const { show, onHide, isOwnAgency, user, clientId } = this.props;

		const {
			clientContacts,
			billingContact,
			additionalContact,
			inputHasError,
			additionalContacts,
			loading,
			submittingInvoice,
			hasError,
			onHold,
			showAddContactModal,
		} = this.state;

		return (
			<Fragment>
				<Modal
					show={show && !onHold}
					onEnter={() => {
						if (isOwnAgency) this.getClientContacts();
					}}
					onHide={() => {
						if (!onHold) onHide();
					}}
					onExited={() => this.setState(this.initialState)}
					id="send-to-contact-modal"
				>
					<Modal.Header closeButton>
						<Modal.Title>{lang.t("Invoice Recipient")}</Modal.Title>
					</Modal.Header>
					<Modal.Body className="modal-body text-center">
						<If condition={isOwnAgency}>
							<If condition={clientContacts.length === 0}>
								<p>{lang.t("No Contacts Available")}</p>
							</If>
							<button
								type="button"
								className="btn btn-default"
								onClick={() => this.openAddContactModal()}
							>
								{lang.t("Add Contact")}
							</button>
							<br />
							<br />
						</If>
						<Choose>
							<When condition={billingContact.label}>
								<div>
									{lang.t("Send Invoice to")}{" "}
									<strong>{billingContact.label}</strong>
								</div>
								<If condition={isOwnAgency}>
									<button
										type="button"
										className="btn btn-link"
										onClick={this.removeBillingContact}
									>
										{lang.t("Change Recipient")}
									</button>
								</If>
							</When>
							<When condition={isOwnAgency && !billingContact.label}>
								<div
									className={classNames("form-group", { "has-error": hasError })}
								>
									<select
										id="billing-contact-select"
										className="form-control"
										aria-label="select contact"
										value={billingContact.value}
										onChange={this.selectBillingContact}
									>
										<option value="">Select invoice recipient</option>
										<For
											each="contact"
											of={clientContacts}
										>
											<option
												key={`contact-option-${contact.value}`}
												value={contact.value}
											>
												{contact.label}
											</option>
										</For>
									</select>
								</div>
							</When>
						</Choose>
						<If condition={!isOwnAgency || billingContact.label}>
							<div>
								<span>BCC:</span>
								<input
									type="email"
									className={`form-control cc-email${inputHasError ? " invalid-field" : ""}`}
									value={additionalContact}
									placeholder="Enter an email address"
									onChange={e =>
										this.setState({
											additionalContact: e.target.value,
											inputHasError: false,
										})
									}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.onSubmitAdditionalContact();
									}}
								/>
								<button
									type="button"
									className="btn btn-link"
									disabled={additionalContact === ""}
									onClick={this.onSubmitAdditionalContact}
								>
									Add
								</button>
							</div>
							<For
								each="contact"
								of={additionalContacts}
							>
								<button
									key={`bcc-${contact}`}
									type="button"
									className="btn btn-rounded"
									onClick={() => this.removeAdditionalContact(contact)}
								>
									{contact}
									<span className="badge">X</span>
								</button>
							</For>
						</If>
						<Loader active={loading || submittingInvoice} />
					</Modal.Body>
					<Modal.Footer>
						<button
							type="button"
							className="btn btn-link"
							onClick={onHide}
						>
							{lang.t("Cancel")}
						</button>
						<button
							type="button"
							className="btn btn-primary"
							disabled={submittingInvoice}
							onClick={this.handleSubmit}
						>
							{lang.t("Send")}
						</button>
					</Modal.Footer>
				</Modal>
				<AddContactModal
					showModal={showAddContactModal}
					closeModal={this.onExitAddContactModal}
					clientId={clientId}
					user={user}
				/>
			</Fragment>
		);
	}
}

SendToContactModal.propTypes = {
	clientId: PropTypes.number,
	onSubmit: PropTypes.func.isRequired,
	show: PropTypes.bool,
	onHide: PropTypes.func,
	isOwnAgency: PropTypes.bool,
	user: PropTypes.object,
};

SendToContactModal.defaultProps = {
	clientId: undefined,
	show: false,
	onHide: () => {},
	isOwnAgency: true,
	user: undefined,
};

export default SendToContactModal;
